import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.baseURL = import.meta.env.PROD
  ? '' // Use relative URLs in production (nginx will proxy /api/* to port 3001)
  : 'http://localhost:3001';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Don't automatically check auth on mount - let protected routes handle it
  // This prevents unnecessary 401 errors on public pages
  useEffect(() => {
    // Only check auth if there's a stored indication that user was logged in
    const shouldCheckAuth = localStorage.getItem('hasAuth') === 'true';
    if (shouldCheckAuth) {
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      // The token is httpOnly, so we can't read it client-side
      // Just try to call the /me endpoint and let the server handle the cookie
      const response = await axios.get('/api/auth/me');
      setUser(response.data.user);
      localStorage.setItem('hasAuth', 'true');
    } catch (err) {
      // Silently handle auth failures - 401s are expected for non-authenticated users
      // Don't log anything to avoid cluttering the console
      setUser(null);
      localStorage.removeItem('hasAuth');
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/register', userData);
      setUser(response.data.user);
      localStorage.setItem('hasAuth', 'true');
      // Token is set as httpOnly cookie by the server
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/login', { email, password });
      setUser(response.data.user);
      localStorage.setItem('hasAuth', 'true');
      // Token is set as httpOnly cookie by the server
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('hasAuth');
      // Cookie is cleared by the server
    }
  };

  const value = {
    user,
    loading,
    error,
    register,
    login,
    logout,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;